"use client";

import { IBaseLabelValue } from "@/interfaces";
import { useRouter } from "next/navigation";
import {
  Accordion,
  CustomButton,
  FlexBetween,
  FlexCenter,
  FlexCol,
} from "../../ui";
import { cn } from "@/lib";
import InputDataContent from "../inputDataContent";
import ResultContent from "../../resultContent";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import { useRef, useState } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedPostalTrackingInquiryResData } from "@/interfaces/inquiries/governmental";

const POST_DETAILS_MAP = {
  date: "تاریخ",
  event: "وضعیت",
  postalNode: "منطقه پستی",
  time: "ساعت",
};

const PostalTrackingInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedPostalTrackingInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const response = data.data.data.data;
  const { tracking_code } = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("postalTracking.inputLabel"),
      value: tracking_code || "",
    },
  ];
  const resultContent: IBaseLabelValue[] = [
    {
      label: t("postalTracking.postType"),
      value: response.postType || "-",
    },
    {
      label: t("postalTracking.sourcePostOffice"),
      value: response.sourcePostOffice || "-",
    },
    {
      label: t("postalTracking.source"),
      value: response.source || "-",
    },
    {
      label: t("postalTracking.destination"),
      value: response.destination || "-",
    },
    {
      label: t("postalTracking.senderName"),
      value: response.senderName || "-",
    },
    {
      label: t("postalTracking.receiverName"),
      value: response.receiverName || "-",
    },
    {
      label: t("postalTracking.sourcePostalCode"),
      value: response.sourcePostalCode || "-",
    },
    {
      label: t("postalTracking.destinationPostalCode"),
      value: response.destinationPostalCode || "-",
    },
    {
      label: t("postalTracking.weight"),
      value: response.weight || "-",
    },
    {
      label: t("postalTracking.totalAmount"),
      value: response.totalAmount || "-",
    },
  ];
  const isAllValuesEmpty = resultContent.every((item) => item.value === "-");
  const createPostDetails = response?.details?.map((detail, i: number) => {
    return {
      title: detail.event,
      key: i + 1,
      date: `${detail.time} - ${detail.date}`,
      data: Object.keys(detail)
        .filter((key) => key !== "id")
        .map((key) => ({
          label: POST_DETAILS_MAP[key as keyof typeof POST_DETAILS_MAP],
          value: detail[key as keyof typeof detail],
        })),
    };
  });
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [openAccordionId, setOpenAccordionId] = useState<number | null>(1);
  return (
    <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          رهگیری مرسوله پستی
        </h2>

        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("postalCodeInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            id="postal-code-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`رهگیری مرسوله ${tracking_code}`}
          />
        </FlexBetween>
        {!isAllValuesEmpty && <ResultContent content={resultContent} />}
        {createPostDetails && (
          <FlexCol>
            {createPostDetails.map((item, i: number) => (
              <div key={i} className="mb-3">
                <Accordion
                  title={`(${item.date}) - ${item.title}`}
                  open={item.key === openAccordionId}
                  hasIcon={false}
                  onOpenChange={(isOpen) => {
                    setOpenAccordionId(isOpen ? item.key : null);
                  }}
                >
                  <ResultContent content={item.data} />
                </Accordion>
              </div>
            ))}
          </FlexCol>
        )}
        <FlexCenter className="mt-4 flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="postal-code-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`رهگیری مرسوله ${tracking_code}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-postal-tracking-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-postal-tracking-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Postal_Tracking_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        {!isAllValuesEmpty && (
          <PdfTemplate
            tableTitle="اطلاعات بسته"
            resultContent={resultContent}
            code={data.code}
            dateTime={data.created_at}
          />
        )}
        {createPostDetails && (
          <>
            {createPostDetails.map((item, i: number) => (
              <PdfTemplate
                key={i}
                tableTitle="جزئیات رهگیری"
                resultContent={item.data}
                code={data.code}
                dateTime={data.created_at}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default PostalTrackingInquiryDetails;
